import {client} from '@/utils/gql'
import {
  CoreRegistrationFieldsFragment,
  DraftingRegistrationFieldsFragment,
  Registration_Request_Bool_Exp,
  Registration_Request_Insert_Input,
  Registration_Request_Order_By,
  Registration_Request_Set_Input,
  RegistrationRequestFieldsForUserFragment,
  ReportViolationRegistrationFieldsFragment,
} from '@/utils/graphqlSdk'
import {NotFoundError, ServerError} from '@/utils/server/errors'

const getByIdOrNull = async (id: string): Promise<CoreRegistrationFieldsFragment | null> =>
  (await client.registrationRequestGetById({id}))?.registration_request_by_pk || null

const getById = async (id: string): Promise<CoreRegistrationFieldsFragment> => {
  const request = await getByIdOrNull(id)

  if (!request) {
    throw new NotFoundError('Registration request does not exist', {id})
  }

  return request
}

const getOneOrNull = async (
  filter: Registration_Request_Bool_Exp,
): Promise<CoreRegistrationFieldsFragment | null> => {
  const request = (await client.registrationRequestGetMany({filter})).registration_request

  if (request.length > 1) {
    throw new ServerError('Get one returned multiple results', {filter})
  }

  return request?.[0] ?? null
}

const getOne = async (
  filter: Registration_Request_Bool_Exp,
): Promise<CoreRegistrationFieldsFragment> => {
  const request = await getOneOrNull(filter)

  if (!request) {
    throw new NotFoundError('Get one returned null', {filter})
  }

  return request
}

const updateOne = async (
  id: string,
  changes: Registration_Request_Set_Input,
): Promise<CoreRegistrationFieldsFragment> => {
  const result = (await client.registrationRequestUpdateOne({id, changes}))
    ?.update_registration_request_by_pk

  if (!result) {
    throw new ServerError('Registration request result not returned', {id})
  }

  return result
}

const createOne = async (
  object: Registration_Request_Insert_Input,
): Promise<CoreRegistrationFieldsFragment> => {
  const request = (await client.registrationRequestInsertOne({object}))
    .insert_registration_request_one

  if (!request) {
    throw new ServerError('Registration request returned null after insert', {object})
  }

  return request
}

const getMany = async (
  filter: Registration_Request_Bool_Exp,
  order_by?: Registration_Request_Order_By,
  offset?: number,
  limit?: number,
): Promise<CoreRegistrationFieldsFragment[]> => {
  return (await client.registrationRequestGetMany({filter, order_by, offset, limit}))
    .registration_request
}

const updateMany = async (
  where: Registration_Request_Bool_Exp,
  set: Registration_Request_Set_Input,
): Promise<CoreRegistrationFieldsFragment[]> => {
  const updates = (await client.registrationRequestUpdateMany({where, set}))
    .update_registration_request

  if (!updates?.returning) {
    throw new ServerError('Registration request update many result not returned', {
      where,
      set,
      updates,
    })
  }

  return updates.returning
}

const getOneForDrafting = async (id: string): Promise<DraftingRegistrationFieldsFragment> => {
  const request =
    (await client.registrationRequestGetOneForDrafting({id}))?.registration_request_by_pk || null

  if (!request) {
    throw new NotFoundError('Registration request does not exist', {id})
  }

  return request
}

const getOneOrNullForReportViolation = async (
  id: string,
): Promise<ReportViolationRegistrationFieldsFragment | null> =>
  (await client.registrationRequestGetOneForReportViolation({id}))?.registration_request_by_pk ||
  null

const getOneForUser = async (id: string): Promise<RegistrationRequestFieldsForUserFragment> => {
  const request =
    (await client.registrationRequestGetOneUser({id}))?.registration_request_by_pk || null

  if (!request) {
    throw new NotFoundError('Registration request does not exist', {id})
  }

  return request
}

const deleteMany = async (where: Registration_Request_Bool_Exp): Promise<number> => {
  const response = (await client.registrationRequestDeleteMany({where})).delete_registration_request

  const affectedRows = response?.affected_rows

  if (typeof affectedRows !== 'number') {
    throw new ServerError('Registration request deleteMany returned null', {where, response})
  }

  return affectedRows
}

const count = async (filter: Registration_Request_Bool_Exp): Promise<number> => {
  const count = (await client.registrationRequestCount({filter})).registration_request_aggregate
    .aggregate?.count

  if (typeof count !== 'number') {
    throw new ServerError('Registration request count returned invalid value', {filter, count})
  }

  return count
}

export const registrationRequestOrm = {
  getById,
  getByIdOrNull,
  getOneOrNull,
  getOne,
  getOneForDrafting,
  getOneOrNullForReportViolation,
  getOneForUser,
  updateOne,
  createOne,
  getMany,
  updateMany,
  deleteMany,
  aggregate: {
    count,
  },
}
